import React from "react";
import { Box, FormHelperText } from "@mui/material";
import { Checkbox } from "@/components/Checkbox";
import { useFeatureFlags } from "@/configs/featureFlag";
import {
  AllMattersAnalyzeOptionsCheckbox,
  type AllMattersAnalyzeOptions,
} from "@/components/AcquireBook/AllMattersAnalyzeOptionsCheckbox";

/** 解析オプションの選択値。送信 payload へそのまま展開する */
export interface BulkAnalyzeSelection {
  withOwnerAnalyze: boolean;
  withAllMattersAnalyze: boolean;
  allMattersAnalyzeOptions: AllMattersAnalyzeOptions;
}

export const INITIAL_BULK_ANALYZE_SELECTION: BulkAnalyzeSelection = {
  withOwnerAnalyze: false,
  withAllMattersAnalyze: false,
  allMattersAnalyzeOptions: {
    includeTitleSection: true,
    includeKoku: true,
    includeOtsuku: true,
    analyzeMethodType: "rule_based",
  },
};

interface BulkAnalyzeOptionsSectionProps {
  value: BulkAnalyzeSelection;
  onChange: (value: BulkAnalyzeSelection) => void;
  /** 所有者事項のチェックが1件もない間は解析チェックを無効化する */
  isOwnerInfoChecked: boolean;
  /** 全部事項系のチェックが1件もない間は解析チェックを無効化する */
  isAllMattersChecked: boolean;
  /** 解析オプション未選択で送信をブロックしたときのみ表示する */
  optionsError: string | null;
}

/**
 * 取得と同時に行う解析の選択節（既存の一括取得画面と同じ体系。表示可否も同じ LD フラグに従う）
 */
export const BulkAnalyzeOptionsSection: React.FC<
  BulkAnalyzeOptionsSectionProps
> = ({
  value,
  onChange,
  isOwnerInfoChecked,
  isAllMattersChecked,
  optionsError,
}) => {
  const { ownerInfoViaScraping, displayCheckboxAllMattersAnalyze } =
    useFeatureFlags();

  return (
    <>
      {ownerInfoViaScraping && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 6, mb: 1 }}>
          <Checkbox
            label="所有者事項の取得と同時に内容を解析する"
            checked={value.withOwnerAnalyze}
            disabled={!isOwnerInfoChecked}
            onChange={(checked) => {
              onChange({ ...value, withOwnerAnalyze: checked });
            }}
          />
        </Box>
      )}
      {displayCheckboxAllMattersAnalyze && (
        <>
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
            <Checkbox
              label="全部事項の取得と同時に内容を解析する"
              checked={value.withAllMattersAnalyze}
              disabled={!isAllMattersChecked}
              onChange={(checked) => {
                onChange({ ...value, withAllMattersAnalyze: checked });
              }}
            />
          </Box>
          {value.withAllMattersAnalyze && (
            <Box
              role="group"
              aria-label="全部事項の解析オプション"
              aria-describedby={
                optionsError != null ? "all-matters-options-error" : undefined
              }
              sx={{
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                alignItems: "center",
                mb: 1,
              }}
            >
              <AllMattersAnalyzeOptionsCheckbox
                disabled={!isAllMattersChecked}
                value={value.allMattersAnalyzeOptions}
                onChange={(options) => {
                  onChange({ ...value, allMattersAnalyzeOptions: options });
                }}
              />
              {optionsError != null && (
                <FormHelperText
                  id="all-matters-options-error"
                  error
                  sx={{ textAlign: "center" }}
                >
                  {optionsError}
                </FormHelperText>
              )}
            </Box>
          )}
        </>
      )}
    </>
  );
};
